import { z } from 'zod';
import { currencySchema, pillarSchema, targetDirectionSchema, targetPeriodSchema, targetStatusSchema, unitSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { isoDateSchema } from '../../weeks/schemas/weeks.schemas.js';
export const TARGET_NAME_MAX_LENGTH = 160;
export const TARGET_VALUE_MESSAGE = 'Target value must be greater than zero.';
/** A KPI a target is linked to, as the caller may see it. */
export const targetKpiRefSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    pillar: pillarSchema,
});
/**
 * A reusable performance target (plan §3.1, §4): a value strictly above zero, a direction, a unit (and currency for
 * Currency) that every linked KPI shares, and an annual or custom period of reporting weeks.
 */
export const targetSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    value: z.number(),
    unit: unitSchema,
    currency: currencySchema.nullable(),
    direction: targetDirectionSchema,
    periodType: targetPeriodSchema,
    /** Set for an ANNUAL target. */
    year: z.number().int().nullable(),
    /** Set for a CUSTOM target. */
    startWeekId: uuidSchema.nullable(),
    endWeekId: uuidSchema.nullable(),
    status: targetStatusSchema,
    /** The resolved inclusive bounds: the first and last reporting Sunday the target covers. */
    fromWeekDate: isoDateSchema,
    toWeekDate: isoDateSchema,
    /** Linked KPIs in the caller's pillars, in pillar and display order. */
    kpis: z.array(targetKpiRefSchema),
    /** Linked KPIs in pillars the caller cannot see — counted, never named. */
    hiddenKpiCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listTargetsInputSchema = z.object({
    /** Matches the target name or a linked KPI's name or code, case-insensitively. */
    search: z.string().trim().max(120).optional(),
});
export const listTargetsOutputSchema = z.object({
    /** Targets with at least one KPI the caller may see: active first, then by period start and name. */
    items: z.array(targetSchema),
});
export const getTargetInputSchema = z.object({ id: uuidSchema });
/** The editable fields; the period and unit rules are checked by the service's `target-rules`, one place for both forms. */
const targetFieldsSchema = z.object({
    name: z.string().trim().min(1, 'A target name is required.').max(TARGET_NAME_MAX_LENGTH, `Use at most ${TARGET_NAME_MAX_LENGTH} characters`),
    value: z.number({ invalid_type_error: TARGET_VALUE_MESSAGE }).finite(TARGET_VALUE_MESSAGE).gt(0, TARGET_VALUE_MESSAGE),
    unit: unitSchema,
    currency: currencySchema.nullable().optional(),
    direction: targetDirectionSchema,
    periodType: targetPeriodSchema,
    year: z.number().int().min(2000).max(2100).nullable().optional(),
    startWeekId: uuidSchema.nullable().optional(),
    endWeekId: uuidSchema.nullable().optional(),
    status: targetStatusSchema,
    /** At least one; each must be visible to the caller and share the target's unit (and currency). */
    kpiIds: z.array(uuidSchema).min(1, 'Link the target to at least one KPI.').max(200),
});
export const createTargetInputSchema = targetFieldsSchema.extend({
    status: targetStatusSchema.default('ACTIVE'),
});
/** Only the supplied fields change; the result is re-validated as a whole, overlap check included. */
export const updateTargetInputSchema = targetFieldsSchema.partial().extend({ id: uuidSchema });
/** The body of the 409 a save answers with when an active target would share a reporting week with another one. */
export const targetOverlapConflictSchema = z.object({
    message: z.string(),
    conflict: z.object({
        targetId: uuidSchema,
        targetName: z.string(),
        kpiId: uuidSchema,
        kpiName: z.string(),
    }),
});
//# sourceMappingURL=targets.schemas.js.map